import { Scheduler } from '../../components/scheduler/Scheduler.jsx'
import {
	getVendorEvents,
	createVendorEvent,
	updateVendorEvent,
	deleteVendorEvent,
	getVendorStaffEvents,
} from '../../features/schedulerSlice.js'
import { getStaffInfo, getVendors } from '../../features/vendorSlice.js'
import { TimeLineHeader } from './TimeLineHeader.jsx'

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toastHelper } from '../../util/toastHelper.js'
import styles from './timeLineStyle.module.scss'
import FilterPopover from '../../components/dataGrid/FilterPopover.jsx'
import ListToolbar from '../../components/dataGrid/ListToolBar.jsx'

export const VendorTimelinePage = ({ isStaff = false }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.auth.user)
	const staff = useSelector((state) => state.vendor.staff)
	const [search, setSearch] = useState('')
	const [innerTab, setInnerTab] = useState(0)
	const [statusFilter, setStatusFilter] = useState([])
	const [staffFilter, setStaffFilter] = useState([])
	const [filterAnchor, setFilterAnchor] = useState(null)
	const [priorityFilter, setPriorityFilter] = useState([])
	const timelineEvents = useSelector(
		(state) => state.scheduler.timelineEvents
	)

	const isOwner = !isStaff && (user?.__t === 'vendor' || user?.type === 'vendor')

	const partyMembers = useMemo(() => {
		if (isStaff) {
			return [
				{
					Id: user._id,
					OwnerText: user.businessName ?? user.firstName,
				},
			]
		}
		const owner = {
			Id: user._id,
			OwnerText: user.businessName || user.firstName || 'Me',
		}
		const staffMembers = (staff || [])
			.filter((s) => (s.staffId || s._id) && s.status === 'active')
			.map((s) => ({
				Id: s.staffId || s._id,
				OwnerText: s.name || s.email,
			}))
		const all = [owner, ...staffMembers]

		if (staffFilter.length > 0)
			return all.filter((p) => staffFilter.includes(String(p.Id)))

		return all
	}, [user, staff, staffFilter, isStaff])

	const staffOptions = useMemo(() => {
		const owner = { value: user._id, label: user.businessName || 'Me' }
		const staffMembers = (staff || [])
			.filter((s) => (s.staffId || s._id) && s.status === 'active')
			.map((s) => ({
				value: s.staffId || s._id,
				label: s.name || s.email,
			}))
		return [owner, ...staffMembers]
	}, [user, staff])

	useEffect(() => {
		if (isStaff) {
			dispatch(getVendorStaffEvents())
			return
		}
		if (isOwner) {
			dispatch(getVendorEvents('isVendorTasks'))
			dispatch(getStaffInfo())
			dispatch(getVendors({ limit: 1000 }))
		} else {
			dispatch(getVendorStaffEvents())
		}
	}, [dispatch, isOwner, isStaff])

	const onActionBegin = async (args) => {
		if (
			args.requestType !== 'eventCreate' &&
			args.requestType !== 'eventChange' &&
			args.requestType !== 'eventRemove'
		) {
			return
		}

		if (isStaff) {
			return toastHelper(
				'You only have view permissions as staff',
				'error'
			)
		}

		try {
			let events = args.data
			args.cancel = true

			if (args.requestType === 'eventCreate') {
				if (!events[0].EventCategory)
					return toastHelper(
						'Please provide an event category',
						'error'
					)

				const start = new Date(events[0].StartTime)
				const end = new Date(events[0].EndTime)

				if (isNaN(start.getTime()) || isNaN(end.getTime()))
					return toastHelper('Invalid date or time selected', 'error')

				if (start >= end)
					return toastHelper(
						'End time must be after start time',
						'error'
					)

				const rawSelection = Array.isArray(events[0].OwnerIds)
					? events[0].OwnerIds
					: [events[0].OwnerIds]
				const syncToCalendar = {
					google: events[0].SyncGoogle !== false,
					outlook: events[0].SyncOutlook !== false,
					ical: events[0].SyncIcal !== false,
				}

				const data = {
					...events[0],
					selectedMembers: rawSelection.filter(
						(id) => id && id !== 'null'
					),
					createdFrom: 'timeline-page',
					title: events[0].Subject,
					comments: events[0].Description,
					status: events[0].status || 'New',
					priority: events[0].priority,
					eventColor: events[0].EventColor,
					syncToCalendar,
					eventCategory: events[0].EventCategory,
				}

				await dispatch(createVendorEvent({ data }))
			}

			if (args.requestType === 'eventChange') {
				const start = new Date(events.StartTime)
				const end = new Date(events.EndTime)

				if (isNaN(start.getTime()) || isNaN(end.getTime()))
					return toastHelper('Invalid date or time selected', 'error')

				if (start >= end)
					return toastHelper(
						'End time must be after start time',
						'error'
					)

				const originalEvent = timelineEvents.find(
					(e) => e._id === events._id
				)
				const originalOwners =
					originalEvent?.OwnerIds?.map((owner) =>
						typeof owner === 'object' ? owner.id : String(owner)
					) || []

				const manageableIds = partyMembers.map((p) => String(p.Id))

				const preservedOwners = originalOwners.filter(
					(id) => !manageableIds.includes(String(id))
				)

				let newSelection = []
				if (events.OwnerIds) {
					newSelection = events.OwnerIds.map((owner) =>
						typeof owner === 'object' ? owner.id : String(owner)
					)
				} else if (Array.isArray(events.OwnerId)) {
					newSelection = events.OwnerId
				} else if (events.OwnerId) newSelection = [events.OwnerId]

				newSelection = newSelection.filter((id) => id && id !== 'null')
				const mergedOwnerIds = [
					...new Set([...preservedOwners, ...newSelection]),
				]
				const syncToCalendar = {
					google: events.SyncGoogle !== false,
					outlook: events.SyncOutlook !== false,
					ical: events.SyncIcal !== false,
				}

				const data = {
					...events,
					OwnerIds: mergedOwnerIds,
					selectedMembers: mergedOwnerIds,
					title: events.Subject,
					comments: events.Description,
					status: events.status,
					priority: events.priority,
					eventColor: events.EventColor,
					syncToCalendar,
					eventCategory: events.EventCategory,
				}
				await dispatch(updateVendorEvent({ id: events._id, data }))
			}

			if (args.requestType === 'eventRemove') {
				let itemsToDelete = Array.isArray(events) ? events : [events]

				const myEventsToDelete = itemsToDelete.filter(
					(e) =>
						e.VendorId && String(e.VendorId) === String(user._id)
				)

				if (myEventsToDelete.length !== itemsToDelete.length)
					toastHelper(
						'You can only delete events created by you.',
						'warning'
					)

				if (myEventsToDelete.length > 0) {
					for (const e of myEventsToDelete) {
						await dispatch(deleteVendorEvent({ id: e._id }))
					}
				}
			}
		} catch (error) {
			console.error('Error saving event:', error)
		}
	}

	const filteredEvents = useMemo(() => {
		const q = search.trim().toLowerCase()
		return (timelineEvents || []).filter((e) => {
			if (
				q &&
				!(
					(e.Subject && e.Subject.toLowerCase().includes(q)) ||
					(e.Description && e.Description.toLowerCase().includes(q))
				)
			) {
				return false
			}
			if (isStaff) {
				if (statusFilter.length && !statusFilter.includes(e.status))
					return false
				if (
					priorityFilter.length &&
					!priorityFilter.includes(e.Priority)
				)
					return false
			} else {
				if (statusFilter.length && !statusFilter.includes(e.status))
					return false
				if (priorityFilter.length && !priorityFilter.includes(e.Priority))
					return false
			}
			return true
		})
	}, [timelineEvents, search, isStaff, statusFilter, priorityFilter])

	const eventSettings = {
		allowAdding: isOwner,
		allowEditing: isOwner,
		allowDeleting: isOwner,
		allowResizing: isOwner,
		allowDrag: isOwner,
		dataSource: filteredEvents.map((event) => ({
			...event,
		})),
		fields: { id: '_id' },
	}

	const chips = isStaff
		? [
				...statusFilter.map((s) => ({
					key: `status-${s}`,
					label: `Status ${s}`,
					onDelete: () =>
						setStatusFilter((prev) => prev.filter((x) => x !== s)),
				})),
				...priorityFilter.map((r) => ({
					key: `priority-${r}`,
					label: `Priority ${r}`,
					onDelete: () =>
						setPriorityFilter((prev) =>
							prev.filter((x) => x !== r)
						),
				})),
			]
		: [
				...staffFilter.map((s) => {
					const member = staffOptions.find(
						(o) => String(o.value) === String(s)
					)
					return {
						key: `staff-${s}`,
						label: member ? member.label : 'Unknown',
						onDelete: () =>
							setStaffFilter((prev) =>
								prev.filter((x) => x !== s)
							),
					}
				}),
				...statusFilter.map((s) => ({
					key: `status-${s}`,
					label: `Status ${s}`,
					onDelete: () =>
						setStatusFilter((prev) => prev.filter((x) => x !== s)),
				})),
				...priorityFilter.map((r) => ({
					key: `priority-${r}`,
					label: `Priority ${r}`,
					onDelete: () =>
						setPriorityFilter((prev) =>
							prev.filter((x) => x !== r)
						),
				})),
			]

	const clearFilters = () => {
		if (isStaff) {
			setStatusFilter([])
			setPriorityFilter([])
			return
		}
		setStaffFilter([])
		setStatusFilter([])
		setPriorityFilter([])
	}

	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)
	const handleInnerTab = (e, tab) => setInnerTab(tab)

	const filterSections = isStaff
		? [
				{
					id: 'status',
					title: 'Status',
					options: ['Completed', 'In Progress', 'Pending'],
					selected: statusFilter,
					onToggle: (val, checked) =>
						setStatusFilter((prev) =>
							checked
								? [...prev, val]
								: prev.filter((x) => x !== val)
						),
				},
				{
					id: 'priority',
					title: 'Priority',
					options: ['High', 'Medium', 'Low'],
					selected: priorityFilter,
					onToggle: (val, checked) =>
						setPriorityFilter((prev) =>
							checked
								? [...prev, val]
								: prev.filter((x) => x !== val)
						),
				},
			]
		: [
				{
					id: 'staff',
					title: 'Staff Member',
					options: staffOptions.map((s) => s.label),
					selected: staffFilter.map(
						(id) =>
							staffOptions.find(
								(o) => String(o.value) === String(id)
							)?.label || id
					),
					onToggle: (label, checked) => {
						const option = staffOptions.find(
							(o) => o.label === label
						)
						if (!option) return
						const val = String(option.value)
						setStaffFilter((prev) =>
							checked
								? [...prev, val]
								: prev.filter((x) => x !== val)
						)
					},
				},
				{
					id: 'status',
					title: 'Status',
					options: ['Completed', 'In Progress', 'Pending'],
					selected: statusFilter,
					onToggle: (val, checked) =>
						setStatusFilter((prev) =>
							checked
								? [...prev, val]
								: prev.filter((x) => x !== val)
						),
				},
				{
					id: 'priority',
					title: 'Priority',
					options: ['High', 'Medium', 'Low'],
					selected: priorityFilter,
					onToggle: (val, checked) =>
						setPriorityFilter((prev) =>
							checked
								? [...prev, val]
								: prev.filter((x) => x !== val)
						),
				},
			]

	return (
		<div className={styles.container}>
			<TimeLineHeader />
			<br />
			<div className={styles.schedulerWrap}>
				<ListToolbar
					title={isStaff ? 'Task' : 'Timeline'}
					searchPlaceholder={
						isStaff ? 'Search task' : 'Search timeline items'
					}
					searchValue={search}
					onSearchChange={setSearch}
					chips={chips}
					showTabes={false}
					isExported={false}
					onClearFilters={clearFilters}
					onOpenFilter={openFilter}
					{...(isStaff
						? {
								innerTab,
								onInnerTabChange: handleInnerTab,
							}
						: {})}
				/>
				<FilterPopover
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={closeFilter}
					onClear={clearFilters}
					onApply={closeFilter}
					sections={filterSections}
				/>
			</div>
			<div className={styles.schedulerWrap}>
				<div className="calendarWrp commonCalendar ">
					<div className="calendarInner">
						<Scheduler
							disableWeddingDateButton={true}
							onActionBegin={onActionBegin}
							eventSettings={eventSettings}
							partyMembers={partyMembers}
							allowDragAndDrop={false}
							allowResizing={false}
							height="77dvh"
							title={`All wedding ${isStaff ? 'events' : 'timeline items'}`}
							isStaff={isStaff}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
